import { useState } from "react";
import { Alert } from "reactstrap";
import { Link } from "react-router-dom";

import ButtonLoader from "components/ButtonLoader";
import { resendConfirmation } from "store/persistence/persistRegistration";

interface Props {
  email?: string;
}

/**
 * Shown right after a non-Google signup. No session exists yet — the user must
 * click the confirmation link we emailed before they can access the app. They
 * can re-send the link from here (see AccountService.confirmEmail /
 * resendSignupConfirmation on the backend).
 */
const RegistrationStepCheckEmail = ({ email }: Props) => {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const onResend = async () => {
    if (!email) {
      return;
    }
    setResending(true);
    try {
      await resendConfirmation(email);
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-width-500">
      <h1 className="h2 font-weight-bold">Confirm your email</h1>
      <div className="text-muted h6 mt-3 mb-4">
        {email ? (
          <>
            We've sent a confirmation link to <strong>{email}</strong>. Click
            the link in that email to finish setting up your account.
          </>
        ) : (
          <>
            We've sent you a confirmation link. Click the link in that email to
            finish setting up your account.
          </>
        )}
      </div>
      <Alert color="info">
        Didn't get the email? Check your spam folder, or resend it below.
      </Alert>
      {resent ? (
        <div className="text-success mt-3" data-testid="confirmation-resent">
          Confirmation email resent. Please check your inbox.
        </div>
      ) : (
        <ButtonLoader
          className="mt-3"
          color="primary"
          size="lg"
          block={true}
          loading={resending}
          disabled={!email}
          onClick={onResend}
        >
          Resend confirmation email
        </ButtonLoader>
      )}
      <div className="additional-cta-section mt-4">
        Already confirmed? <Link to="/login">Login</Link>
      </div>
    </div>
  );
};

export default RegistrationStepCheckEmail;
